/**
 * Compliance reference tables — verbatim numerical limits from the two
 * benchmarks named on the /compliance page.
 *
 * SANS 241:2015 — SABS. Drinking-water specification.
 *   Consolidated across Table 1 (microbiological) and Table 2 (chemical,
 *   physical, aesthetic, operational, radiological).
 *
 * WHO GDWQ — WHO Guidelines for Drinking-water Quality, 4th ed.
 *   incorporating the 1st and 2nd addenda (2017 / 2022).
 *
 * Values reproduced for reference only. In an audit, cite the primary
 * source document (see REFERENCE_META.sources).
 */

export const REFERENCE_META = {
  sans241: {
    eyebrow: "SANS 241:2015",
    title: "SANS 241:2015 — full determinand list.",
    seoTitle: "SANS 241:2015 parameter list",
    seoDescription: "Full determinand list of SANS 241:2015 — microbiological, chemical, physical, aesthetic, operational and radiological limits for drinking water in South Africa.",
    lede: "The complete list of determinands and numerical limits published in SANS 241:2015 across Table 1 (microbiological) and Table 2 (chemical). Compliance is measured at the point of consumption — SANS 241:2015 Section 5.6.2.",
    provenance: "SANS 241-1:2015 · Drinking water Part 1 · SABS · edition 2",
    sources: [
      { label: "SANS 241:2015 · purchase from SABS Store", url: "https://store.sabs.co.za/sans-241-1-2015" },
      { label: "SANS 241 Ed 7 (draft revision, public TC document)", url: "https://www.umngeni-uthukela.co.za/wp-content/uploads/2024/07/SANS241ED7_TC147_DSS_2.pdf" },
      { label: "SABS Technical Committee 147 · draft SANS 241 Ed 7 (May 2022)", url: "https://gwd.org.za/sites/default/files/2022-05/SANS241ED7_TC147_DSS_1%20-%20draft%20May%202022.pdf" },
      { label: "Water Research Commission · potable water supply reference", url: "https://www.wrc.org.za/wp-content/uploads/mdocs/TT%20415%20Potable%20water%20supply.pdf" },
    ],
    disclaimer:
      "Numerical values reproduced for reference. In every audit, sampling or compliance report the primary source (SANS 241:2015 as published by SABS) is the authoritative record. Determinands not tabulated here that appear on a specific site's water-quality risk assessment default to the WHO Guidelines for Drinking-water Quality (SANS 241:2015 Section 4).",
  },
  who: {
    eyebrow: "WHO GDWQ · 4th ed.",
    title: "WHO Guidelines for Drinking-water Quality — reference values.",
    seoTitle: "WHO Guidelines for Drinking-water Quality · reference values",
    seoDescription: "Health-based guideline values from the WHO Guidelines for Drinking-water Quality (4th ed., incorporating the 1st and 2nd addenda). Used as the international benchmark for determinands not tabulated in SANS 241.",
    lede: "Health-based reference values from the WHO Guidelines for Drinking-water Quality — the benchmark SANS 241:2015 nominates for any determinand not tabulated in the South African standard.",
    provenance: "WHO GDWQ · 4th edition (2017) with 1st (2018) and 2nd (2022) addenda",
    sources: [
      { label: "WHO GDWQ · 4th ed. + amendments · full PDF", url: "https://iris.who.int/bitstream/handle/10665/254637/9789241549950-eng.pdf" },
      { label: "WHO Guidelines landing page", url: "https://www.who.int/publications/i/item/9789241549950" },
    ],
    disclaimer:
      "Values shown are health-based guideline values (GV) from the WHO Guidelines for Drinking-water Quality (WHO, 2022). Where a WHO GV is provisional or based on a lower detection limit than the corresponding SANS 241 numeric limit, the stricter of the two applies to the site.",
  },
};

export const SANS_241_TABLES = [
  {
    eyebrow: "SANS 241:2015 · Table 1",
    name: "Microbiological determinands",
    notes: "Acute-health class unless otherwise stated. Sampled at the point of consumption.",
    columns: ["Determinand", "Unit", "Numerical limit", "Class"],
    rows: [
      ["Escherichia coli", "count / 100 mL", "Not detected", "Acute health"],
      ["Faecal coliforms (alternative)", "count / 100 mL", "Not detected", "Acute health"],
      ["Total coliforms", "count / 100 mL", "≤ 10", "Operational"],
      ["Heterotrophic plate count", "count / mL", "≤ 1 000", "Operational"],
      ["Somatic coliphages", "count / 10 mL", "Not detected", "Acute health"],
      ["Cryptosporidium spp.", "count / 10 L", "Not detected", "Acute health"],
      ["Giardia spp.", "count / 10 L", "Not detected", "Acute health"],
      ["Enteric viruses", "count / 10 L", "Not detected", "Acute health"],
    ],
  },
  {
    eyebrow: "SANS 241:2015 · Table 2",
    name: "Physical & aesthetic determinands",
    columns: ["Determinand", "Unit", "Numerical limit", "Class"],
    rows: [
      ["Colour", "mg/L Pt-Co", "≤ 15", "Aesthetic"],
      ["Conductivity at 25 °C", "mS/m", "≤ 170", "Aesthetic"],
      ["Total dissolved solids (TDS)", "mg/L", "≤ 1 200", "Aesthetic"],
      ["Turbidity (operational)", "NTU", "≤ 1", "Operational"],
      ["Turbidity (aesthetic)", "NTU", "≤ 5", "Aesthetic"],
      ["pH at 25 °C", "pH units", "≥ 5.0 to ≤ 9.7", "Operational"],
      ["Taste", "—", "No objectionable", "Aesthetic"],
      ["Odour", "—", "No objectionable", "Aesthetic"],
    ],
  },
  {
    eyebrow: "SANS 241:2015 · Table 2",
    name: "Chemical determinands — macro",
    columns: ["Determinand", "Unit", "Numerical limit", "Class"],
    rows: [
      ["Nitrate as N", "mg/L", "≤ 11", "Acute health"],
      ["Nitrite as N", "mg/L", "≤ 0.9", "Acute health"],
      ["Sulphate as SO₄²⁻ (acute)", "mg/L", "≤ 500", "Acute health"],
      ["Sulphate as SO₄²⁻ (aesthetic)", "mg/L", "≤ 250", "Aesthetic"],
      ["Fluoride", "mg/L", "≤ 1.5", "Chronic health"],
      ["Ammonia as N", "mg/L", "≤ 1.5", "Aesthetic"],
      ["Chloride", "mg/L", "≤ 300", "Aesthetic"],
      ["Sodium", "mg/L", "≤ 200", "Aesthetic"],
      ["Zinc", "mg/L", "≤ 5", "Aesthetic"],
      ["Iron (chronic)", "µg/L", "≤ 2 000", "Chronic health"],
      ["Iron (aesthetic)", "µg/L", "≤ 300", "Aesthetic"],
      ["Manganese (chronic)", "µg/L", "≤ 400", "Chronic health"],
      ["Manganese (aesthetic)", "µg/L", "≤ 100", "Aesthetic"],
      ["Free chlorine", "mg/L", "≤ 5", "Chronic health"],
      ["Monochloramine", "mg/L", "≤ 3", "Chronic health"],
    ],
  },
  {
    eyebrow: "SANS 241:2015 · Table 2",
    name: "Chemical determinands — trace metals",
    notes: "All chronic-health class unless otherwise stated.",
    columns: ["Determinand", "Unit", "Numerical limit", "Class"],
    rows: [
      ["Antimony", "µg/L", "≤ 20", "Chronic health"],
      ["Arsenic", "µg/L", "≤ 10", "Chronic health"],
      ["Barium", "µg/L", "≤ 700", "Chronic health"],
      ["Boron", "µg/L", "≤ 2 400", "Chronic health"],
      ["Cadmium", "µg/L", "≤ 3", "Chronic health"],
      ["Chromium (total)", "µg/L", "≤ 50", "Chronic health"],
      ["Copper", "µg/L", "≤ 2 000", "Chronic health"],
      ["Cyanide (free)", "µg/L", "≤ 200", "Acute health"],
      ["Lead", "µg/L", "≤ 10", "Chronic health"],
      ["Mercury", "µg/L", "≤ 6", "Chronic health"],
      ["Nickel", "µg/L", "≤ 70", "Chronic health"],
      ["Selenium", "µg/L", "≤ 40", "Chronic health"],
      ["Uranium", "µg/L", "≤ 30", "Chronic health"],
      ["Vanadium", "µg/L", "≤ 200", "Chronic health"],
      ["Aluminium", "µg/L", "≤ 300", "Operational"],
    ],
  },
  {
    eyebrow: "SANS 241:2015 · Table 2",
    name: "Chemical determinands — organics & disinfection by-products",
    columns: ["Determinand", "Unit", "Numerical limit", "Class"],
    rows: [
      ["Total organic carbon (TOC)", "mg/L", "≤ 10", "Chronic health"],
      ["Trihalomethanes (total)", "sum of ratios", "≤ 1", "Chronic health"],
      ["Chloroform (component)", "µg/L", "≤ 300", "Chronic health"],
      ["Bromoform (component)", "µg/L", "≤ 100", "Chronic health"],
      ["Dibromochloromethane (component)", "µg/L", "≤ 100", "Chronic health"],
      ["Bromodichloromethane (component)", "µg/L", "≤ 60", "Chronic health"],
      ["Chlorophenols (per compound)", "µg/L", "≤ 1", "Aesthetic"],
      ["Microcystin-LR", "µg/L", "≤ 1", "Chronic health"],
    ],
  },
  {
    eyebrow: "SANS 241:2015 · Table 2",
    name: "Radiological determinands",
    columns: ["Determinand", "Unit", "Numerical limit", "Class"],
    rows: [
      ["Gross alpha activity", "Bq/L", "≤ 0.5", "Chronic health"],
      ["Gross beta activity", "Bq/L", "≤ 1.0", "Chronic health"],
    ],
  },
];

export const WHO_TABLES = [
  {
    eyebrow: "WHO GDWQ · Table 8.5",
    name: "Verification of microbial quality",
    columns: ["Organism", "Guideline value"],
    rows: [
      ["Escherichia coli or thermotolerant coliforms", "Must not be detectable in any 100 mL sample"],
      ["Total coliforms (distribution system, treated water)", "Must not be detectable in any 100 mL sample"],
    ],
  },
  {
    eyebrow: "WHO GDWQ · Table 8.19 (excerpt)",
    name: "Inorganic health-based guideline values",
    notes: "P = provisional guideline value.",
    columns: ["Determinand", "Guideline value"],
    rows: [
      ["Antimony", "0.020 mg/L"],
      ["Arsenic", "0.010 mg/L (P)"],
      ["Barium", "1.3 mg/L"],
      ["Boron", "2.4 mg/L"],
      ["Cadmium", "0.003 mg/L"],
      ["Chromium (total)", "0.050 mg/L (P)"],
      ["Copper", "2 mg/L"],
      ["Cyanide (long-term)", "0.070 mg/L"],
      ["Fluoride", "1.5 mg/L"],
      ["Lead", "0.010 mg/L (P, AMDL)"],
      ["Manganese", "0.080 mg/L"],
      ["Mercury (inorganic)", "0.006 mg/L"],
      ["Molybdenum", "0.070 mg/L"],
      ["Nickel", "0.070 mg/L"],
      ["Nitrate (as NO₃⁻)", "50 mg/L (short-term)"],
      ["Nitrite (as NO₂⁻)", "3 mg/L (short-term)"],
      ["Selenium", "0.040 mg/L (P)"],
      ["Uranium", "0.030 mg/L (P)"],
    ],
  },
  {
    eyebrow: "WHO GDWQ · Table 8.31 (excerpt)",
    name: "Disinfection by-products — guideline values",
    columns: ["Determinand", "Guideline value"],
    rows: [
      ["Bromate", "0.010 mg/L (P)"],
      ["Bromodichloromethane", "0.060 mg/L"],
      ["Bromoform", "0.100 mg/L"],
      ["Chlorate", "0.700 mg/L (P)"],
      ["Chlorite", "0.700 mg/L (P)"],
      ["Chloroform", "0.300 mg/L"],
      ["Dibromoacetonitrile", "0.070 mg/L"],
      ["Dibromochloromethane", "0.100 mg/L"],
      ["Dichloroacetate", "0.050 mg/L (P)"],
      ["Trichloroacetate", "0.200 mg/L"],
      ["Trihalomethanes (sum of ratios)", "≤ 1"],
    ],
  },
  {
    eyebrow: "WHO GDWQ · Chapter 10",
    name: "Aesthetic parameters (acceptability)",
    notes: "Values below which most consumers find water aesthetically acceptable.",
    columns: ["Determinand", "Level of concern"],
    rows: [
      ["Colour", "15 TCU"],
      ["Turbidity", "≤ 1 NTU (verification); ≤ 4 NTU (acceptable)"],
      ["pH", "6.5 – 8.5"],
      ["Chloride", "250 mg/L"],
      ["Sulphate", "250 mg/L"],
      ["Sodium", "200 mg/L"],
      ["Aluminium", "0.1 – 0.2 mg/L (depending on plant)"],
      ["Iron", "0.3 mg/L"],
      ["Manganese", "0.1 mg/L"],
      ["Zinc", "3 mg/L"],
      ["Total dissolved solids", "1 000 mg/L (indicative)"],
    ],
  },
];
